import { memo, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { getActiveUser } from "services/api/users";
import { startCurrentMfaChallengeAPI, updateCurrentMfaAPI } from "services/api/users/api";
import { getApiErrorByCode } from "services/api/transformation";
import { MfaChallenge } from "services/auth";

import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";
import { TextField } from "ui/components/TextField/TextField";
import { useTranslate } from "helpers";

import { MfaEnableCardProps } from "./types";

const CODE_LENGTH = 6;
const CODE_PATTERN = /^\d{6}$/;

/**
 * Uključivanje dvostruke provjere: kod se šalje čim se prozor otvori, a zaštita se pali tek
 * kad korisnik taj kod vrati. Tako nitko ne ostane zaključan s adresom na koju pošta ne stiže.
 */
const MfaEnableCard: React.FC<MfaEnableCardProps> = (props) => {
  const { show, onSuccess, onClose } = props;

  const { t } = useTranslate();

  const { user } = useSelector(getActiveUser);
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);
  const [code, setCode] = useState<string>("");
  const [error, setError] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const requestCode = useCallback(async () => {
    setIsLoading(true);
    setCode("");
    setError(undefined);

    try {
      setChallenge(await startCurrentMfaChallengeAPI());
    } catch {
      // Poruka je otišla kroz snackbar; prozor ostaje da korisnik može pokušati ponovno.
      setChallenge(null);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (show) {
      requestCode();
    } else {
      setChallenge(null);
      setCode("");
      setError(undefined);
    }
  }, [show, requestCode]);

  const handleCodeChange = useCallback((e: any) => {
    const digits = String(e?.target?.value ?? "").replace(/\D/g, "").slice(0, CODE_LENGTH);
    setCode(digits);
    setError(undefined);
  }, []);

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    if (!challenge) {
      await requestCode();
      return;
    }

    if (!CODE_PATTERN.test(code)) {
      setError(`Upišite ${CODE_LENGTH}-znamenkasti kod iz e-pošte`);
      return;
    }

    setIsLoading(true);

    try {
      await updateCurrentMfaAPI({
        enabled: true,
        challengeId: challenge.challengeId,
        code,
      });
      setIsLoading(false);
      if (onSuccess) onSuccess();
      return;
    } catch (err: any) {
      const invalidCode = getApiErrorByCode(err, "MfaCodeInvalid");

      if (invalidCode) {
        setError(invalidCode);
        setCode("");
      } else if (
        getApiErrorByCode(err, "MfaChallengeNotFound") ||
        getApiErrorByCode(err, "MfaTooManyAttempts")
      ) {
        // Izazov više ne postoji; novi kod umjesto zatvaranja prozora.
        setChallenge(null);
      }
    }

    setIsLoading(false);
  };

  return (
    <Modal show={show} onHide={onClose} position="centered" size="small">
      <ModalHeader
        title="Uključivanje dvostruke provjere prijave"
        icon="/media/images/add-icon.svg"
        onHide={onClose}
      />
      <ModalBody>
        <div className="flex gap-4">
          <div className="w-full m-auto">
            <div className="flex gap-3 h-full">
              <form noValidate className="text-left flex-1 p-4" onSubmit={handleFormSubmit}>
                <p className="text-sm text-slate-600 mb-4">
                  {challenge ? (
                    <>
                      Poslali smo kod na <span className="font-medium">{challenge.email}</span>.
                      Upišite ga da potvrdite adresu i uključite zaštitu.
                    </>
                  ) : (
                    <>
                      Kod će biti poslan na <span className="font-medium">{user?.email}</span>.
                    </>
                  )}
                </p>
                <div className="flex gap-3 mb-3">
                  <div className="flex-1">
                    <TextField
                      type="text"
                      name="code"
                      label="Kod iz e-pošte"
                      placeholder="123456"
                      autoComplete="one-time-code"
                      onChange={handleCodeChange}
                      error={error}
                      value={code}
                      maxLength={CODE_LENGTH}
                      disabled={!challenge}
                      disableDebounce
                      required
                    />
                  </div>
                </div>
                <div className="mb-3 text-sm">
                  <button
                    type="button"
                    onClick={requestCode}
                    disabled={isLoading}
                    className="font-medium text-blue-500 hover:text-blue-400 disabled:text-slate-400"
                  >
                    {challenge ? "Pošalji novi kod" : "Pošalji kod"}
                  </button>
                </div>
                <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                  <Button
                    variant="outlined"
                    children={t("admin.close")}
                    onClick={onClose}
                  />
                  <Button
                    variant="primary"
                    children="Uključi"
                    onClick={handleFormSubmit}
                    disabled={isLoading || !challenge}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>

        <Loader isLoading={isLoading ?? false}> </Loader>
      </ModalBody>
    </Modal>
  );
};

export default memo(MfaEnableCard);
